import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useToast } from '../ui/Toast';
import { api, type Wallet } from '../../lib/api';
import { TxHashField } from './TxHashField';
import { isCashierEntityAllowed } from './constants';

interface TransferFormProps {
    onSubmit: () => void;
}

const transferFormSchema = z.object({
    amount: z
        .string()
        .min(1, 'Amount is required')
        .refine(
            (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
            'Must be a positive number'
        ),
    walletFromId: z.string().min(1, 'Please select a source wallet'),
    walletToId: z.string().min(1, 'Please select a destination wallet'),
    date: z.string().optional(),
    comment: z.string().max(200).optional(),
    txHashOut: z.string().optional(),
    txHashIn: z.string().optional(),
});

type TransferFormData = z.infer<typeof transferFormSchema>;

const CURRENCY_ICONS: Record<string, string> = {
    RUB: '₽',
    EUR: '€',
    USD: '$',
    GBP: '£',
    USDT: '₮',
    BTC: '₿',
    ETH: 'Ξ',
    USDC: '$',
};

export function TransferForm({ onSubmit }: TransferFormProps) {
    const { t } = useTranslation();
    const { currentEntity } = useEntity();
    const toast = useToast();

    const [wallets, setWallets] = useState<Wallet[]>([]);
    const [walletsLoading, setWalletsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { errors },
    } = useForm<TransferFormData>({
        resolver: zodResolver(transferFormSchema),
        defaultValues: {
            amount: '',
            walletFromId: '',
            walletToId: '',
            date: new Date().toISOString().split('T')[0],
            comment: '',
            txHashOut: '',
            txHashIn: '',
        },
    });

    const walletFromId = watch('walletFromId');
    const walletToId = watch('walletToId');

    const selectedFromWallet = wallets.find((w) => w.id === walletFromId);
    const selectedToWallet = wallets.find((w) => w.id === walletToId);
    const currency = selectedFromWallet?.currencyCode || '';

    // USDT and USDC are treated as interchangeable stablecoins for transfer
    const STABLECOIN_GROUP = ['USDT', 'USDC'];
    function getCompatibleCodes(code: string): string[] {
        const upper = code.toUpperCase();
        return STABLECOIN_GROUP.includes(upper) ? STABLECOIN_GROUP : [upper];
    }
    const compatibleCodes = currency ? getCompatibleCodes(currency) : null;

    const transferToOptions = wallets.filter(
      w => w.id !== walletFromId &&
           (!walletFromId || (compatibleCodes ?? []).includes(w.currencyCode?.toUpperCase()))
    );

    const fetchWallets = useCallback(async () => {
        if (!currentEntity?.id) return;
        if (!isCashierEntityAllowed(currentEntity.code)) {
            setWallets([]);
            setWalletsLoading(false);
            return;
        }
        try {
            setWalletsLoading(true);
            // Transfers allow cross-group transfers — load ALL entity wallets
            const searchParams = new URLSearchParams({ allWallets: 'true' });
            const res = await fetch(`/api/admin/wallets?${searchParams}`, {
                credentials: 'include',
                headers: { 'X-Entity-Id': currentEntity.id },
            });
            if (!res.ok) throw new Error('Failed to load wallets');
            const result = await res.json() as { data: Wallet[] };
            setWallets(result.data.filter((w) => w.isActive));
        } catch {
            toast.error('Failed to load wallets');
        } finally {
            setWalletsLoading(false);
        }
    }, [currentEntity?.id, currentEntity?.code, toast]);

    useEffect(() => {
        fetchWallets();
    }, [fetchWallets]);

    const onFormSubmit = async (data: TransferFormData) => {
        if (!currentEntity?.id) {
            toast.error('No entity selected');
            return;
        }
        if (!currency) {
            toast.error('Invalid currency');
            return;
        }

        setIsSubmitting(true);
        try {
            await api.transactions.create(currentEntity.id, {
                type: 'transfer',
                amount: data.amount,
                currency,
                walletFromId: data.walletFromId,
                walletToId: data.walletToId,
                ...(data.comment && { comment: data.comment }),
                ...(data.date && { transactionDate: data.date }),
                ...(data.txHashOut && { txHashOut: data.txHashOut }),
                ...(data.txHashIn && { txHashIn: data.txHashIn }),
            });

            toast.success(t('cashier.createOperation.transferSuccess', 'Transfer recorded'));

            reset({
                amount: '',
                walletFromId: '',
                walletToId: '',
                date: new Date().toISOString().split('T')[0],
                comment: '',
                txHashOut: '',
                txHashIn: '',
            });

            onSubmit();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to record transfer');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-5">
            {/* Info banner */}
            <div style={{
                background: 'var(--color-surface1, #e6e9ef)',
                border: '1px solid var(--color-blue, #1e66f5)',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '0.8rem',
                color: 'var(--color-text)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
            }}>
                <span style={{ fontSize: '1rem' }}>ℹ️</span>
                <span>Трансферы позволяют переводить средства между <strong>любыми</strong> кошельками всех групп, при условии совпадения валюты.</span>
            </div>
            {/* Amount + Source/Destination Wallets */}
            <div>
                <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                    {t('cashier.createOperation.transferAmount', 'Amount to Transfer')}
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                    {/* Amount input */}
                    <div className="flex-1">
                        <div className="relative h-full">
                            {currency && (
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-foreground-muted pointer-events-none">
                                    {CURRENCY_ICONS[currency.toUpperCase()] || currency}
                                </span>
                            )}
                            <input
                                type="text"
                                inputMode="decimal"
                                className={`
                                  w-full h-full min-h-[46px] py-2 border rounded-lg text-sm text-foreground bg-surface
                                  focus:outline-none focus:border-ctp-teal placeholder:text-foreground-muted
                                  ${currency ? 'pl-8 pr-3' : 'px-3'}
                                  ${errors.amount ? 'border-ctp-red' : 'border-border'}
                                `}
                                placeholder="0.00"
                                {...register('amount')}
                            />
                        </div>
                        {errors.amount && (
                            <p className="text-[11px] text-ctp-red mt-1">{errors.amount.message}</p>
                        )}
                    </div>

                    {/* From Wallet selector */}
                    <div className="flex-[1.5]">
                        <select
                            className={`
                              w-full h-full min-h-[46px] py-2 px-3 border rounded-lg text-sm text-foreground bg-surface
                              focus:outline-none focus:border-ctp-teal
                              ${errors.walletFromId ? 'border-ctp-red' : 'border-border'}
                            `}
                            {...register('walletFromId', {
                                onChange: () => {
                                    setValue('walletToId', '');
                                }
                            })}
                        >
                            <option value="">
                                {walletsLoading
                                    ? t('loading', 'Loading...')
                                    : t('cashier.createOperation.transferSource', 'Select From Wallet')}
                            </option>
                            {wallets.map((w) => (
                                <option key={w.id} value={w.id}>
                                    {CURRENCY_ICONS[w.currencyCode.toUpperCase()] || w.currencyCode}{' '}
                                    {w.name}
                                </option>
                            ))}
                        </select>
                        {errors.walletFromId && (
                            <p className="text-[11px] text-ctp-red mt-1">{errors.walletFromId.message}</p>
                        )}
                    </div>

                    {/* To Wallet selector */}
                    <div className="flex-[1.5]">
                        <select
                            className={`
                              w-full h-full min-h-[46px] py-2 px-3 border rounded-lg text-sm text-foreground bg-surface
                              focus:outline-none focus:border-ctp-teal
                              ${errors.walletToId ? 'border-ctp-red' : 'border-border'}
                            `}
                            {...register('walletToId')}
                            disabled={walletsLoading || !walletFromId}
                        >
                            <option value="">
                                {walletsLoading
                                    ? t('loading', 'Loading...')
                                    : t('cashier.createOperation.transferDestination', 'Select To Wallet')}
                            </option>
                            {transferToOptions.map((w) => (
                                <option key={w.id} value={w.id}>
                                    {CURRENCY_ICONS[w.currencyCode.toUpperCase()] || w.currencyCode}{' '}
                                    {w.name}
                                </option>
                            ))}
                        </select>
                        {errors.walletToId && (
                            <p className="text-[11px] text-ctp-red mt-1">{errors.walletToId.message}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Date + Comment row */}
            <div className="flex flex-col sm:flex-row gap-4">
                {/* Date */}
                <div className="sm:w-44">
                    <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                        {t('cashier.createOperation.date', 'Date')}
                    </label>
                    <input
                        type="date"
                        className="w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-teal"
                        {...register('date')}
                    />
                    <p className="text-[11px] text-foreground-muted mt-1.5">
                        {t('cashier.createOperation.dateHint', 'Optional. Defaults to today.')}
                    </p>
                </div>

                {/* Comment */}
                <div className="flex-1">
                    <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                        {t('cashier.createOperation.comment', 'Comment')}
                    </label>
                    <input
                        type="text"
                        className="w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-teal placeholder:text-foreground-muted"
                        placeholder={t('cashier.createOperation.addNote', 'Add a note...')}
                        maxLength={200}
                        {...register('comment')}
                    />
                </div>
            </div>

            {/* TX Hash fields for source/destination crypto wallets */}
            {(selectedFromWallet?.type === 'crypto' || selectedToWallet?.type === 'crypto') && (
                <div className="flex flex-col sm:flex-row gap-4">
                    {selectedFromWallet?.type === 'crypto' && (
                        <div className="flex-1">
                            <TxHashField
                                label={t('cashier.txHash.labelOut', 'TX Hash (OUT)')}
                                walletAddress={selectedFromWallet.address}
                                walletType={selectedFromWallet.type}
                                value={watch('txHashOut') || ''}
                                onChange={(v) => setValue('txHashOut', v)}
                            />
                        </div>
                    )}
                    {selectedToWallet?.type === 'crypto' && (
                        <div className="flex-1">
                            <TxHashField
                                label={t('cashier.txHash.labelIn', 'TX Hash (IN)')}
                                walletAddress={selectedToWallet.address}
                                walletType={selectedToWallet.type}
                                value={watch('txHashIn') || ''}
                                onChange={(v) => setValue('txHashIn', v)}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Submit */}
            <button
                type="submit"
                disabled={isSubmitting || walletsLoading}
                className="
                    flex items-center justify-center gap-2.5 w-full py-4 px-6
                    text-white border-none rounded-xl text-[15px] font-bold
                    cursor-pointer transition-all active:scale-95 mt-2 shadow-lg
                    bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600
                    disabled:opacity-60 disabled:cursor-not-allowed
                "
            >
                <Send size={18} />
                {isSubmitting
                    ? t('cashier.createOperation.processing', 'Processing...')
                    : t('cashier.createOperation.transferSubmit', 'RECORD TRANSFER')}
            </button>
        </form>
    );
}
